import { cloneDeep } from 'lodash'
import { applicantStore, settingsStore } from '@/store'
import { Application, ProceedApplicationPayload } from '@/commons/types'
import { ErrorUtils } from '@/commons/utils/error'
import { StringUtils } from '@/commons/utils/string'
import { ENVIRONMENT } from '@/commons/configuration'
import axios, { AxiosResponse, CancelTokenSource } from 'axios'
import { RequestClientErrorStatus } from '@/commons/constants/error'
import authenticatedAxios from '@/commons/configuration/authenticated.axios'
import i18n from '@/commons/translations/i18n'
import { StatusResponse } from '@/commons/constants/status.response'
import { ValidDocumentType } from '@/commons/constants/application'

function resolveBiometryOnSave (application: Application): void {
    const imageUrlPrefix = /^data:.+;base64,/
    if (application.biometry.photo) {
        application.biometry.photo = application.biometry.photo.replace(imageUrlPrefix, '')
    }
    if (application.biometry.signature) {
        application.biometry.signature = application.biometry.signature.replace(imageUrlPrefix, '')
    }
}

function resolveDocumentsOnSave (application: Application): void {
    if (!application.citizenPassport?.reason) {
        application.citizenPassport = null
    }
    if (!application.idCard?.reason) {
        application.idCard = null
    }
}

export class ApplicationService {
    static async saveApplication (applicationType: string): Promise<void> {
        const url = ENVIRONMENT.proceedUrl.replace('{APPLICATION_TYPE}', applicationType)

        // For double clicking
        if (applicantStore.requestCancelToken) {
            applicantStore.requestCancelToken.cancel()
        }

        applicantStore.requestCancelToken = axios.CancelToken.source()

        applicantStore.application.contact.phoneNumber = StringUtils.trimString(applicantStore.application.contact.phoneNumber)
        const application: Application = cloneDeep(applicantStore.application)
        resolveBiometryOnSave(application)
        resolveDocumentsOnSave(application)
        application.applicant.personCode = applicantStore.personCode
        const response = await ApplicationService.storeApplication(url, application, applicantStore.requestCancelToken)
        applicantStore.getApplication.uuid = response.data.uuid
    }

    static storeApplication (url: string, application: Application, cancelToken: CancelTokenSource): Promise<AxiosResponse> {
        const payload: ProceedApplicationPayload = {
            application: application,
            locale: settingsStore.verifiedLocale
        }
        return authenticatedAxios.post(url, payload, {
            cancelToken: cancelToken.token
        })
    }

    static async fetchOngoingApplication (
        personCode: string,
        cancelToken: CancelTokenSource,
        applicationType: string): Promise<void> {
        try {
            const url = ENVIRONMENT.unPaidApplicationUrl.replace('{APPLICATION_TYPE}', applicationType)
            const response = await ApplicationService.fetchSavedApplication(url, personCode, cancelToken)
            if (response.data.uuid === null) {
                applicantStore.application.uuid = null
                return
            }
            applicantStore.application = response.data as Application

            // for unlikely reason uuid is a falsy value
            if (!applicantStore.application.uuid) {
                applicantStore.application.uuid = null
            }
        } catch (e) {
            if (axios.isCancel(e)) {
                return
            }
            const error = ErrorUtils.getRequestErrorResponse(e)
            if (error?.status === RequestClientErrorStatus.NOT_FOUND) {
                applicantStore.application.uuid = null
                return
            }
            throw ErrorUtils.getRequestErrorEntity(error)
        }
    }

    static fetchSavedApplication (
        url: string,
        personCode: string,
        cancelToken: CancelTokenSource): Promise<AxiosResponse> {
        return authenticatedAxios.get(url, {
            params: {
                personCode: personCode
            },
            cancelToken: cancelToken.token
        })
    }

    static async getPreviousDocumentApplications (): Promise<Record<string, string>[]> {
        const url = ENVIRONMENT.previousApplicationsUrl
        const response = await authenticatedAxios.get(url, {
            params: {
                personCode: applicantStore.personCode
            }
        })
        return response.data
    }

    static getDocumentTypeAsString (documentType: string): string {
        if (documentType === ValidDocumentType.CITIZEN_PASSPORT) {
            return i18n.t('previousApplications.citizenPassportApplication').toString()
        } else if (documentType === ValidDocumentType.ID_CARD) {
            return i18n.t('previousApplications.citizenIdCardApplication').toString()
        } else if (documentType === ValidDocumentType.ADDITIONAL_PASSPORT) {
            return i18n.t('previousApplications.additionalPassportApplication').toString()
        } else if (documentType === ValidDocumentType.COMBO) {
            return i18n.t('previousApplications.comboApplication').toString()
        } else {
            return i18n.t('previousApplications.unknownApplication').toString()
        }
    }

    static getStatusFromApplicationStatus (applicationStatus: string): string {
        applicationStatus = applicationStatus.toLowerCase()
        let key = 'previousApplications.status.unknown'
        if (applicationStatus === StatusResponse.BEING_ENTERED || applicationStatus === StatusResponse.IN_PROCEEDING) {
            key = 'previousApplications.status.inProceeding'
        } else if (applicationStatus === StatusResponse.PENDING.toString()) {
            key = 'previousApplications.status.pending'
        } else if (applicationStatus === StatusResponse.SCANNED || applicationStatus === StatusResponse.QUALITY_CONTROL ||
            applicationStatus === StatusResponse.READY_FOR_ARCHIVING ||
            applicationStatus === StatusResponse.ARCHIVED || applicationStatus === StatusResponse.DECISION_MADE) {
            key = 'previousApplications.status.decisionMade'
        } else if (applicationStatus === StatusResponse.CANCELLED) {
            key = 'previousApplications.status.paymentCancelled'
        } else if (applicationStatus === StatusResponse.PAYMENT) {
            key = 'previousApplications.status.paymentInProgress'
        } else if (applicationStatus === StatusResponse.IN_PROCESS) {
            key = 'previousApplications.status.inProcess'
        }
        return i18n.t(key).toString()
    }

    static formatJsonDate (jsonDate: string): string {
        const date = new Date(jsonDate)
        const day = date.getDate()
        const month = date.getMonth() + 1
        const year = date.getFullYear()
        return `${day.toString().padStart(2, '0')}.${month.toString().padStart(2, '0')}.${year}`
    }
}
